import React from "react";
import moment from "moment/moment";

interface SprintBarsProps {
  spans: any[],
  boxWidth: number,
  duration: number
}

const SprintBars: React.FC<SprintBarsProps> = ({ spans, boxWidth, duration }) => {
  return (
    <>
      {spans.map((span: any, sprintIndex: number) => {
        const sprintWidth = (span.endIdx - span.startIdx + 1) * boxWidth;
        const leftOffset = span.startIdx * boxWidth;

        let bgGradient, textClasses, statusText, shadowStyle, borderStyle, overlayGradient;

        // Determine sprint styling based on status (past/current/future)
        if (span.sprint.status === 'current') {
          // Current sprint - Green glassy gradient (more prominent)
          bgGradient = 'linear-gradient(135deg, rgb(34 197 94 / 21%), rgb(34 197 81 / 46%))';
          textClasses = 'text-green-800 dark:text-green-100';
          borderStyle = '1px solid rgba(255, 255, 255, 0.2)';
          shadowStyle = '0 1px 3px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
          overlayGradient = 'linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.1))';
          statusText = '(current)';
        } else if (span.sprint.status === 'future') {
          // Future sprint - Blue glassy gradient
          bgGradient = 'linear-gradient(135deg, rgba(59, 130, 246, 0.65), rgba(59, 130, 246, 0.35))';
          textClasses = 'text-blue-900 dark:text-blue-100';
          borderStyle = '1px solid rgba(255, 255, 255, 0.18)';
          shadowStyle = '0 1px 3px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.12)';
          overlayGradient = 'linear-gradient(to bottom, rgba(0,0,0,0.04), rgba(0,0,0,0.08))';
          statusText = '(future)';
        } else {
          // Past sprint - Gray glassy gradient (default for past status)
          bgGradient = 'linear-gradient(135deg, rgba(107, 114, 128, 0.45), rgba(107, 114, 128, 0.25))';
          textClasses = 'text-gray-700 dark:text-gray-300';
          borderStyle = '1px solid rgba(255, 255, 255, 0.12)';
          shadowStyle = '0 1px 2px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)';
          overlayGradient = 'linear-gradient(to bottom, rgba(0,0,0,0.02), rgba(0,0,0,0.06))';
          statusText = '(past)';
        }

        return (
          <div
            key={`sprint-${sprintIndex}`}
            className={`absolute flex items-center justify-center ${textClasses}`}
            style={{
              left: `${leftOffset}px`,
              width: sprintWidth,
              minWidth: sprintWidth,
              height: '32px',
              top: 0,
              background: bgGradient,
              border: borderStyle,
              boxShadow: shadowStyle,
              backdropFilter: 'blur(8px)',
              borderRadius: '0'
            }}
            title={`${span.sprint.name} (${moment(span.startISO).format('ll')} → ${moment(span.endISO).format('ll')})`}
          >
            {/* Subtle overlay for additional glass effect */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: overlayGradient,
                opacity: 0.3
              }}
            />
            <span className="text-xs font-medium truncate px-2 text-center relative z-10">
              {span.sprint.name} {statusText}
            </span>
          </div>
        );
      })}

      {/* Background grid for proper borders */}
      <div className="flex bg-white dark:bg-[#1a1f2e]">
        {Array.from({ length: duration }).map((_, index) => (
          <div
            key={`sprint-grid-${index}`}
            className="border-b border-gray-300 dark:border-[#2d3748] border-r border-r-gray-300 dark:border-r-[#2d3748]"
            style={{
              width: `${boxWidth}px`,
              minWidth: `${boxWidth}px`,
              height: '32px'
            }}
          />
        ))}
      </div>
    </>
  );
};

export default SprintBars;